import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button } from 'antd';

import messages from '../../constants/messages';
import { TABLE_CONFIG_KEYS } from '../../constants/table-configurations';

import { useModal } from '../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

import { createRotation, deleteRotation, updateRotation } from '../../redux/actions/geofence-rotations';
import { clearRotationDetail } from '../../redux/reducers/geofence-rotations';

import AppTable from '../../components/AppTable';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../components/action_notifier';

import RotationForm from './RotationForm';
import { useGrazingColumns } from './useGrazingColumns';

const AppModal = lazy(() => import('../../components/AppModal'));
const UnsavedChangesModal = lazy(() => import('../../components/ui/UnsavedChangesModal'));
const RotationDetailsModal = lazy(() => import('../PasturesCalendar/RotationDetailsModal'));

function RotationTable({ geofences, fetchData, handleSearchChange }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const formCreateRef = useRef(null);
  const formRef = useRef(null);

  const { data: rotations, loading: rotationsLoading, rotationDetail } = useSelector(state => state.rotation);

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [createModalVisible, openCreateModal, closeCreateModal] = useModal();

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const [viewModalVisible, openViewModal, closeViewModal] = useModal();
  const [isEditMode, setIsEditMode] = useState(false);

  const [editedRotationValid, setEditedRotationValid] = useState(false);

  const openAddModal = () => {
    formCreateRef.current?.resetFields();
    openCreateModal();
  };

  const handleRowsSelect = useCallback(selected => {
    setSelectedRows(selected);
  }, []);

  const actions = [
    {
      label: (
        <Button color="default" size="small" variant="link" onClick={openAddModal}>
          Add rotation
        </Button>
      )
    },
    {
      label: (
        <Button color="default" size="small" variant="link" disabled={!selectedRows?.length} onClick={openArchiveModal}>
          Archive rotation
        </Button>
      )
    }
  ];

  const handleCancel = useCallback(() => {
    if (isFormDirty) {
      triggerUnsavedChangesPrompt();
    } else {
      if (rotationDetail) {
        dispatch(clearRotationDetail());
        closeViewModal();
        setIsEditMode(false);
      } else {
        closeCreateModal();
      }
    }
  }, [isFormDirty, rotationDetail]);

  const handleArchiveRotation = useCallback(async () => {
    if (!selectedRows?.length) return;

    try {
      const promises = selectedRows.map(id => dispatch(deleteRotation(id)).unwrap());

      await Promise.all(promises);
      fetchData();

      setSelectedRows([]);
      successToastHandler(messages.DELETED);
      closeArchiveModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_DELETE_DATA);
    }
  }, [selectedRows]);

  const handleCreateRotation = useCallback(async () => {
    try {
      const data = await formCreateRef.current?.validateFields();

      await dispatch(createRotation({ ...data })).unwrap();
      fetchData();

      setIsFormDirty(false);
      successToastHandler(messages.CREATED);
      closeCreateModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_CREATE_DATA);
    }
  }, [formCreateRef]);

  const updateRotationRecord = useCallback(async ([data]) => {
    try {
      const isForageSpeciesSame = Array.isArray(data.forage_species);
      let payload = { ...data };

      if (!isForageSpeciesSame) {
        const forage_species =
          typeof data.forage_species === 'string' && data.forage_species?.length
            ? data.forage_species
                .split(',')
                .map(s => s.trim())
                .filter(Boolean)
            : [];

        payload = { ...data, forage_species };
      }

      if (data.assigned_groups.length) {
        payload.assigned_groups = data.assigned_groups?.map(g => g.label_id);
      }

      await dispatch(updateRotation({ id: data?.id, data: payload })).unwrap();
      await fetchData();

      setIsFormDirty(false);
      successToastHandler(messages.UPDATED);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }
  }, []);

  const handleUpdateRotation = useCallback(async () => {
    try {
      const data = await formRef.current?.validateFields();

      await dispatch(
        updateRotation({
          id: rotationDetail?.id,
          data
        })
      ).unwrap();
      await fetchData();

      setIsFormDirty(false);
      successToastHandler(messages.UPDATED);
      dispatch(clearRotationDetail());
      closeViewModal();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_UPDATE_DATA);
    }
  }, [formRef, rotationDetail]);

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (rotationDetail) {
          if (editedRotationValid) {
            await handleUpdateRotation();
          } else {
            warnToastHandler(messages.DATA_REQUIRED);
            formRef.current?.validateFields();
            return false;
          }
          return true;
        }

        if (submittable) {
          await handleCreateRotation();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        if (rotationDetail) {
          closeViewModal();
          setIsEditMode(false);
          setIsFormDirty(false);
          dispatch(clearRotationDetail());
        } else {
          formCreateRef.current?.resetFields();
          setIsFormDirty(false);
          closeCreateModal();
        }
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  useEffect(() => {
    if (rotationDetail) {
      openViewModal();
    }
  }, [rotationDetail]);

  const onDeleteFromDetailsView = useCallback(() => {
    setSelectedRows(rotationDetail);
    openArchiveModal();
  }, [rotationDetail]);

  const handleSwitchEditMode = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const columns = useGrazingColumns();

  const rotationsWithGeofenceData = useMemo(() => {
    return rotations.map(rotation => {
      const geofence = geofences?.find(g => g?.id === rotation.geofence_id);
      return {
        ...rotation,
        pasture: { id: geofence?.id, name: geofence?.name },
        expected_tracked_livestock: rotation?.geofence?.expected_livestock,
        actual_tracked_livestock: rotation?.geofence?.actual_livestock,
        lastRotation: geofence?.lastRotation ?? null,
        nextRotation: geofence?.nextRotation ?? null
      };
    });
  }, [geofences, rotations]);

  return (
    <>
      {NavigationPrompt}
      <AppTable
        headerClass="py-2"
        searchPlaceholder="Search Rotation"
        title="Grazing"
        description="Grazing rotations"
        actions={actions}
        baseColumns={columns}
        dataSource={rotationsWithGeofenceData}
        loading={rotationsLoading}
        tableConfigKey={TABLE_CONFIG_KEYS.GRAZING}
        handleOnSelectRecords={handleRowsSelect}
        handleSearchChange={handleSearchChange}
        updateTableData={updateRotationRecord}
      />

      <Suspense fallback={null}>
        <AppModal
          isOpen={createModalVisible}
          confirmButtonText="Create"
          confirmButtonDisabled={!submittable}
          title="Add Rotation"
          handleCancel={handleCancel}
          handleConfirm={handleCreateRotation}>
          <RotationForm
            className="py-4"
            formRef={formCreateRef}
            onChange={setIsFormDirty}
            setSubmittable={setSubmittable}
          />
        </AppModal>

        <AppModal
          isOpen={archiveModalVisible}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive Rotation"
          handleCancel={closeArchiveModal}
          handleConfirm={handleArchiveRotation}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('rotation(s)')}</div>
        </AppModal>

        <UnsavedChangesModal
          isOpen={modalProps.visible}
          handleCancel={modalProps.onCancel}
          handleConfirm={modalProps.onSave}
          handleDiscard={modalProps.onDiscard}
        />

        <RotationDetailsModal
          formRef={formRef}
          isEditMode={isEditMode}
          isOpen={viewModalVisible}
          selectedRotation={rotationDetail}
          handleCancel={handleCancel}
          handleDelete={onDeleteFromDetailsView}
          handleEdit={handleSwitchEditMode}
          handleUpdate={handleUpdateRotation}
          onChange={setIsFormDirty}
          updateSubmittable={setEditedRotationValid}
        />
      </Suspense>
    </>
  );
}

export default memo(RotationTable);
